/**
 * Base Counter Interface
 * All exercise counters must extend this class and implement the required methods.
 *
 * This provides a consistent interface for different counting algorithms
 * (Skipping, Pickleball, etc.) that can be swapped via the UI.
 */
export class BaseCounter {
  constructor() {
    if (this.constructor === BaseCounter) {
      throw new Error('BaseCounter is an abstract class and cannot be instantiated directly');
    }
    this.repCount = 0;
  }

  /**
   * Get the unique identifier for this counter type
   * @returns {string} Counter type ID (e.g., 'skipping', 'pickleball')
   */
  static get id() {
    throw new Error('Counter must implement static id getter');
  }

  /**
   * Get the display name for this counter type
   * @returns {string} Human-readable name (e.g., 'Jump Rope', 'Pickleball Swings')
   */
  static get displayName() {
    throw new Error('Counter must implement static displayName getter');
  }

  /**
   * Get description of what this counter detects
   * @returns {string} Description of the counting algorithm
   */
  static get description() {
    throw new Error('Counter must implement static description getter');
  }

  /**
   * Update the counter with new pose keypoints
   * @param {Array} keypoints - Array of pose keypoints from the detector
   * @param {number} timestamp - Current timestamp in milliseconds
   * @returns {number} Current rep count
   */
  update(keypoints, timestamp = Date.now()) {
    throw new Error('Counter must implement update() method');
  }

  /**
   * Reset the counter to initial state
   */
  reset() {
    throw new Error('Counter must implement reset() method');
  }

  /**
   * Get current state for UI display
   * @returns {Object} State object with at minimum { repCount, isInAir, isCalibrating, groundThreshold }
   */
  getState() {
    throw new Error('Counter must implement getState() method');
  }

  /**
   * Load a previously saved state (for debugging/testing)
   * @param {Object} state - State object from getFullState()
   */
  loadState(state) {
    throw new Error('Counter must implement loadState() method');
  }

  /**
   * Get full internal state for saving/debugging
   * @returns {Object} Complete internal state
   */
  getFullState() {
    throw new Error('Counter must implement getFullState() method');
  }
}
